import React from 'react';
import { Archive, Pencil, Share2, X } from 'lucide-react';
import type { GameSession } from '@business-game/shared';
import { AdminBuildStamp } from './AdminBuildStamp';

export type SessionModalTab = 'edit' | 'share';

const getSessionStatus = (session: GameSession) => {
  if (session.archivedAt) return { label: 'Archivée', dotClass: 'bg-slate-500', badgeClass: 'bg-slate-100 text-slate-700' };
  if (session.isClosed) return { label: 'Fermée', dotClass: 'bg-slate-400', badgeClass: 'bg-slate-100 text-slate-600' };
  if ((session.players ?? 0) > 0) return { label: 'Jouée', dotClass: 'bg-emerald-500', badgeClass: 'bg-emerald-50 text-emerald-700' };
  if (session.startDate && new Date(session.startDate) > new Date()) {
    return { label: 'Planifiée', dotClass: 'bg-blue-500', badgeClass: 'bg-blue-50 text-blue-700' };
  }
  return { label: 'Ouverte', dotClass: 'bg-indigo-500', badgeClass: 'bg-indigo-50 text-indigo-700' };
};

type SessionModalProps = {
  mode: 'create' | 'edit';
  onClose: () => void;
  session?: GameSession | null;
  programSlotLabel?: string;
  tab?: SessionModalTab;
  onTabChange?: (tab: SessionModalTab) => void;
  children: React.ReactNode;
  footer: React.ReactNode;
};

export const SessionModal: React.FC<SessionModalProps> = ({
  mode,
  onClose,
  session,
  programSlotLabel,
  tab = 'edit',
  onTabChange,
  children,
  footer,
}) => {
  const status = session ? getSessionStatus(session) : null;
  const titlePlain = mode === 'create' ? 'Nouvelle' : 'Modifier';
  const titleAccent = mode === 'create' ? 'session' : 'séance';

  return (
    <div className="fixed inset-0 z-[100]" aria-modal="true" role="dialog">
      <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm transition-opacity" onClick={onClose} />

      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
          <div className="relative flex max-h-[92vh] w-full max-w-[720px] flex-col overflow-hidden rounded-[20px] bg-white shadow-2xl shadow-indigo-500/15 ring-1 ring-slate-200/80">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-5 top-5 z-20 rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div className="border-b border-slate-100 px-7 pb-5 pt-7">
                <div className="flex flex-wrap items-start justify-between gap-4 pr-10">
                  <div className="min-w-0">
                    <h2 className="text-[26px] font-bold leading-none tracking-tight text-slate-900">
                      {titlePlain}{' '}
                      <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
                        {titleAccent}
                      </span>
                    </h2>

                    {mode === 'edit' && session ? (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="rounded-[9px] bg-indigo-50 px-2.5 py-1 font-mono text-sm font-bold tracking-wide text-indigo-600">
                          {session.code}
                        </span>
                        {status ? (
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${status.badgeClass}`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${status.dotClass}`} />
                            {status.label}
                          </span>
                        ) : null}
                        {programSlotLabel ? (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-sm font-semibold text-slate-500">{programSlotLabel}</span>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  {mode === 'edit' && onTabChange ? (
                    <div className="inline-flex shrink-0 rounded-[13px] border border-slate-200 bg-slate-50 p-1">
                      <button
                        type="button"
                        onClick={() => onTabChange('edit')}
                        className={[
                          'inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-bold transition-all',
                          tab === 'edit'
                            ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-100'
                            : 'text-slate-600 hover:text-slate-900',
                        ].join(' ')}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => onTabChange('share')}
                        className={[
                          'inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-bold transition-all',
                          tab === 'share'
                            ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-100'
                            : 'text-slate-600 hover:text-slate-900',
                        ].join(' ')}
                      >
                        <Share2 className="h-3.5 w-3.5" />
                        Partager
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex-1 px-7 py-6">{children}</div>

              <div className="border-t border-slate-100 bg-slate-50/80 px-7 py-5">{footer}</div>

              <div className="px-7 pb-4">
                <AdminBuildStamp />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type SessionModalFooterProps = {
  mode: 'create' | 'edit';
  onCancel: () => void;
  submitLabel?: string;
  submitDisabled?: boolean;
  onArchive?: () => void;
  canArchive?: boolean;
  formId?: string;
};

export const SessionModalFooter: React.FC<SessionModalFooterProps> = ({
  mode,
  onCancel,
  submitLabel,
  submitDisabled = false,
  onArchive,
  canArchive = false,
  formId,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    {mode === 'edit' && canArchive && onArchive ? (
      <button
        type="button"
        onClick={onArchive}
        className="inline-flex items-center gap-2 rounded-xl px-1 py-2 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-800"
      >
        <Archive className="h-4 w-4" />
        Archiver la séance
      </button>
    ) : (
      <span />
    )}

    <div className="ml-auto flex gap-2.5">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-[13px] border-[1.5px] border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50"
      >
        Annuler
      </button>
      {formId ? (
        <button
          type="submit"
          form={formId}
          disabled={submitDisabled}
          className="inline-flex items-center justify-center gap-2 rounded-[13px] bg-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-200/80 transition-all hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitLabel ?? (mode === 'create' ? 'Créer' : 'Enregistrer')}
        </button>
      ) : (
        <button
          type="button"
          onClick={onCancel}
          className="rounded-[13px] border-[1.5px] border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50"
        >
          Fermer
        </button>
      )}
    </div>
  </div>
);
